<template>
    <el-menu
        router
        :default-active="activeResource"
        :collapse="isSidebarMinimized"
        :collapse-transition="false"
        :popper-offset="0"
        text-color="#ffffff"
        active-text-color="#ffffff"
        class="main-menu"
        popper-class="main-menu"
        ref="menu"
    >
        <el-menu-item :index="$router.resolve({name: 'HomePage'}).path" :route="$router.resolve({name: 'HomePage'})">
            <el-icon><House /></el-icon>
            <span>Home</span>
        </el-menu-item>

        <template v-for="resource in displayedResources" :key="resource.name">
            <el-menu-item v-if="!resource.nested?.length" :index="resource.route?.path" :route="resource.route">
                <el-icon v-if="resource.icon"><component :is="resource.icon" /></el-icon>
                <span>{{ resource.name }}</span>
            </el-menu-item>
            <el-sub-menu :index="resource.route?.path" v-else>
                <template #title>
                    <el-icon v-if="resource.icon"><component :is="resource.icon" /></el-icon>
                    <span>{{ resource.name }}</span>
                </template>
                <el-menu-item v-for="child in resource.nested" :key="child.name" :index="child.route?.fullPath" :route="child.route">
                    <el-icon v-if="child.icon"><component :is="child.icon" /></el-icon>
                    <span>{{ child.name }}</span>
                </el-menu-item>
            </el-sub-menu>
        </template>
        <el-menu-item :index="$router.resolve({name: 'ContactPage'}).path" :route="$router.resolve({name: 'ContactPage'})">
            <el-icon><Message /></el-icon>
            <span>Contact</span>
        </el-menu-item>
    </el-menu>
</template>

<script>
import { Document, DocumentChecked, Odometer, Avatar, User, OfficeBuilding, Reading, Management, MessageBox } from '@element-plus/icons-vue'

import {mapGetters} from 'vuex'

export default {
    name: 'NavigationMenu',
    components: {Document, DocumentChecked, Odometer, Avatar, User, OfficeBuilding, Reading, Management, MessageBox},
    data: () => {
        return {
            activeRoute: undefined
        }
    },
    computed: {
        ...mapGetters('ui', [ 'isSidebarMinimized' ]),
        ...mapGetters('auth', ['resources']),

        displayedResources () {
            // const ownFacilityId = this.resources.find(r => r.name === 'national-facility')?.id

            const resources = this.facilitiesResources
                .concat(this.reviewResources)
                .concat(this.staticResources)

            if (this.administratorResources?.length > 0)
                return resources.concat([{
                    name: "Administrative Tools",
                    icon: Avatar,
                    route: this.$router.resolve({ name: 'AdminParentRoute' }),
                    nested: this.administratorResources
                }])

            return resources
        },

        flatResources () {
            let flat = []
            this.displayedResources.forEach(resource => {
                flat.push(resource)

                if (resource.children) {
                    flat = flat.concat(resource.children)
                }
            })

            return flat
        },

        activeResource () {
            const isResourceActive = (resource) => {
                // First filter based on name
                const sameNameRoutes = this.$route.matched.filter ( r => r.name == resource.route?.name )

                // If the name criteria is fullfilled, then check if the URL really is the same
                // taking parameters into consideration
                if (sameNameRoutes.length > 0) {
                    return sameNameRoutes
                    .map (route => {
                        return this.$router.resolve({ name: route.name, params: this.$route.params })
                    })
                    .some ( route => route.fullPath == resource.route?.fullPath )
                }

                return false
            }

            const activeResources = this.flatResources.toReversed().map (resource => {
                let activeRoutes = []
                if (resource.nested) {
                    activeRoutes = resource.nested.filter ( nested => isResourceActive (nested) ).map ( nested => nested.route )
                }

                if (isResourceActive (resource) ) {
                    activeRoutes.push (resource.route)
                }

                return activeRoutes
            })
            .flat()

            const val = activeResources.at(0)

            return val?.path || this.$route.path
        },

        facilitiesResources () {
            const facilityResources = (facility) => {
                const KNOWN = {
                    "profile": {
                        name: "Profile",
                        icon: Document,
                        route: facility.id ? this.$router.resolve({ name: 'FacilityProfile', params: { facilityId: facility.id } }) : undefined
                    },
                    "acceptance": {
                        name: 'Acceptance',
                        icon: DocumentChecked,
                        route: facility.id ? this.$router.resolve({ name: 'FacilityIa', params: { facilityId: facility.id } }) : undefined
                    },
                    "quality-assurance": {
                        name: "Quality Assurance",
                        icon: Odometer,
                        route: facility.id ? this.$router.resolve({ name: 'FacilityQa', params: { facilityId: facility.id } }) : undefined
                    },
                }

                return facility?.children
                    ?.map(resource => this.matchResource(resource, KNOWN))
                    .filter (resource => resource !== undefined) || []
            }

           return this.resources?.facilities?.map(facility => ({
                name: facility.name,
                icon: OfficeBuilding,
                route: facility.id ? this.$router.resolve({ name: 'NationalFacilityPage', params: { facilityId: facility.id } }) : undefined,
                nested: facilityResources (facility)
            })) || []
        },

        reviewResources () {
            const KNOWN = {
                "initial-acceptance": {
                    name: "Review Acceptance",
                    icon: DocumentChecked,
                    route:  this.$router.resolve({ name: 'CARSFacilitiesIAReview' })
                },
                "quality-assurance": {
                    name: "Review Quality Assurance",
                    icon: Odometer,
                    route:  this.$router.resolve({ name: 'CARSFacilitiesQAList' })
                },
            }
            return this.resources?.review
            ?.map(resource => this.matchResource(resource, KNOWN))
            .filter (resource => resource !== undefined) || []
        },
        staticResources () {
            return this.resources?.static_pages?.map(page => ({
                name: page.name,
                icon: Management,
                route: page.id ? this.$router.resolve({ name: 'StaticPage', params: { slug: page.id } }) : undefined
            })) || []
        },
        administratorResources () {
            const KNOWN = {
                users: {
                    name: "Users",
                    icon: User,
                    route:  this.$router.resolve({ name: 'AdminEditUsers' })
                },
                "national-facilities": {
                    name: "National Facilities",
                    icon: OfficeBuilding,
                    route:  this.$router.resolve({ name: 'AdminFacilities' })
                },
                pages: {
                    name: "Static pages",
                    icon: Reading,
                    route: this.$router.resolve({ name: 'AdminPages' })
                },
                // email: {
                //     name: "Email System",
                //     icon: MessageBox,
                //     route: undefined
                // }
            }
            return this.resources?.administrator
            ?.map(resource => this.matchResource(resource, KNOWN))
            .filter (resource => resource !== undefined) || []
        },
    },
    watch: {
        $route (to) {
            this.activeRoute = to.name
        }
    },
    methods: {
        matchResource (resource, accepted) {
            const known = accepted[resource?.name] || undefined

            if (resource?.children?.length) {
                if (known?.nested) {
                    known.nested = resource.children.map(child => this.matchResource(child, known?.nested)).filter(child => child !== undefined)
                } else {
                    known.nested = undefined
                }
            }

            if (!known?.route && !known?.nested) {
                return undefined
            }

            return known
        }
    },
    mounted () {
        this.activeRoute = this.$route.name
    }
}
</script>

<style>
#header > ul.main-menu {
    border-bottom: none;
}

#header > ul.main-menu li.is-active {
    border-bottom: none;
}

#main-container > aside > ul {
    height: 100%;
}

#main-container > .el-aside .main-menu,
#main-container > .el-aside .main-menu .el-menu {
    background: linear-gradient(to right, rgb(50, 46, 61), rgb(27, 26, 31));
    --el-menu-hover-bg-color: rgb(27, 26, 31);
}

#main-container > .el-aside > .main-menu li.is-active {
    background-color: #1f3864;
}

.el-menu--popup-container.main-menu,
.el-menu--popup-container.main-menu .el-menu {
    background: linear-gradient(to right, rgb(50, 46, 61), rgb(27, 26, 31));
    --el-menu-hover-bg-color: rgb(27, 26, 31);
}

</style>